import { useCallback, useEffect, useState } from 'react'
import { Check, Copy, Link2, Lock, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge, EmptyState, ErrorNote, Spinner } from '@/components/ui/misc'
import { api, ApiError, type ShareLink } from '@/lib/api'
import { formatDateTime, formatRelative } from '@/lib/format'
import { useAuth } from '@/features/auth/AuthProvider'

/**
 * Every public link in one place.
 *
 * Visible to Viewers as well as Editors on purpose: knowing which files are
 * exposed to the internet is not privileged information within the team, and
 * an unnoticed permanent link is exactly the thing this page exists to catch.
 */
export function SharesPage() {
  const { canEdit } = useAuth()
  const [shares, setShares] = useState<ShareLink[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    api
      .shares({ includeInactive })
      .then((res) => setShares(res.shares))
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : 'Could not load share links'),
      )
      .finally(() => setLoading(false))
  }, [includeInactive])

  useEffect(load, [load])

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(url)
      window.setTimeout(() => setCopied(null), 2000)
    } catch {
      // Clipboard may be blocked; the URL is still visible in the row.
    }
  }

  async function revoke(id: string) {
    try {
      await api.revokeShare(id)
      load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke the link')
    }
  }

  const activeForever = shares.filter((s) => s.active && s.expiresAt === null).length

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
        <div>
          <h1 className="text-lg font-semibold">Public links</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Files reachable from the internet without signing in. Revoking takes effect
            immediately.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="h-4 w-4 accent-[var(--accent)]"
          />
          Show revoked
        </label>
      </div>

      {activeForever > 0 && (
        <div className="border-b border-border bg-warning/10 px-5 py-2.5 text-sm">
          <strong>{activeForever}</strong> active link
          {activeForever === 1 ? '' : 's'} never expire
          {activeForever === 1 ? 's' : ''}. These stay usable until revoked.
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error && (
          <div className="p-5">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <Spinner className="h-5 w-5" />
          </div>
        )}

        {!loading && shares.length === 0 && !error && (
          <EmptyState
            icon={<Link2 className="h-8 w-8" />}
            title="No public links"
            description="Open a file and choose Share to create one."
          />
        )}

        {shares.length > 0 && (
          <ul className="divide-y divide-border">
            {shares.map((share) => (
              <li key={share.id} className="flex items-start justify-between gap-4 px-5 py-3">
                <div className="min-w-0 space-y-1">
                  <p className="truncate font-mono text-sm" title={share.key}>
                    {share.key}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {share.active ? (
                      <Badge tone="success">Active</Badge>
                    ) : (
                      <Badge tone="danger">{share.problem ?? 'Inactive'}</Badge>
                    )}
                    {share.hasPassword && (
                      <Badge tone="neutral">
                        <Lock className="h-3 w-3" />
                        Password
                      </Badge>
                    )}
                    <span>
                      {share.downloadCount}
                      {share.maxDownloads !== null && ` / ${share.maxDownloads}`} download
                      {share.downloadCount === 1 ? '' : 's'}
                    </span>
                    <span>
                      {share.expiresAt
                        ? `expires ${formatDateTime(share.expiresAt)}`
                        : 'never expires'}
                    </span>
                    <span>by {share.createdBy}</span>
                    {share.lastAccessAt && <span>last used {formatRelative(share.lastAccessAt)}</span>}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => void copy(share.url)}
                    aria-label="Copy link"
                    title={share.url}
                  >
                    {copied === share.url ? (
                      <Check className="h-4 w-4 text-success" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                  {canEdit && !share.revokedAt && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => void revoke(share.id)}
                      aria-label="Revoke link"
                      title="Revoke immediately"
                    >
                      <Trash2 className="h-4 w-4 text-danger" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
